import { filter } from "lodash";
import type { entityProcessConfig } from "../common/entity-process/signalr-process-config";
import store from "../root/store/store";
import { Enums } from "../common/helper/enums";
import { contactEntity } from "../common/model/entity/contact-entity";
import { invoiceEntity } from "../common/model/entity/invoice-entity";
import { technicianEntity } from "../common/model/entity/technician-entity";
import { ticketEntity } from "../common/model/entity/ticket-entity";
import { applyClientPortalUnreadStateOnTicketSync } from "../common/entity-service/ticket-entity-service";
import { EntityManager } from "../common/services/entity-manager";
import { bumpTicketSidebarListRevision } from "../ticket/ticket-sidebar-list-refresh";
import TechnicianServices from "../common/services/technician-service";
import ticketService from "../common/services/ticket-service";
import { ErrorTemplateBuilder } from "./error-template-builder";
import { customStatusEntity } from "../common/model/entity/custom-status-entity";

export class EntityTickManager {
  private static seconds: number = 10000;
  private static timeTick: number = 621355968000000000;

  static async checkTechnicianTick() {
    const latestTick = technicianEntity.query().max("tick");
    try {
      let tickResponse = await TechnicianServices.getTechnicianByTick(
        latestTick,
      );
      if (tickResponse != null) {
        await technicianEntity.insertOrUpdate({ data: tickResponse });
      }
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntityTickManager.checkTechnicianTick",
          e,
        ),
      );
    }
  }

  static async checkTicketTick(payload: any) {
    const _tick = filter(payload, ["entityName", "Ticket"]);
    if (_tick.length > 0) {
      const serverLatestTick = _tick[0].tick;
      const latestTick = ticketEntity.query().max("tick");
      if (serverLatestTick > latestTick) {
        try {
          let hasAllTicketsTag: boolean =
            store.getters["ContactModule/getHasAllTicketsTag"];
          let clientId: number | null =
            store.getters["ContactModule/getClientId"];
          let clientIdValue: number = clientId == null ? 0 : clientId;
          const obj = {
            tick: latestTick,
            clientId: clientIdValue,
            hasAllTicketsTag: hasAllTicketsTag,
          };
          const [result]: [any, entityProcessConfig | null] =
            await EntityManager.getOnlyAPCallForEntity(
              { eventId: Enums.StateEntitySync.InsertOrUpdate },
              Enums.EntitySyncManager[Enums.EntitySyncManager.CheckTicketTick],
              obj,
            );
          if (result && result.tickets) {
            const selectedTicketId = store.getters[
              "ticketDetailModule/getTicketId"
            ] as string;
            const viewerContactId = store.getters[
              "ContactModule/getContactId"
            ] as number;
            const tickets = (result.tickets as any[]).map((ticket: any) => {
              const row = { ...ticket };
              const existing = ticketEntity.find(String(row.ticketId ?? ""));
              applyClientPortalUnreadStateOnTicketSync(
                row,
                selectedTicketId,
                existing?.isUnread,
                viewerContactId,
              );
              return row;
            });
            await ticketEntity.insertOrUpdate({ data: tickets });
            bumpTicketSidebarListRevision();
          }
          if (result && result.ticketsToDelete) {
            ticketEntity.delete((ticket: any) => {
              return result.ticketsToDelete.includes(ticket.ticketId);
            });
          }
        } catch (e) {
          console.error(
            ErrorTemplateBuilder.getCatchText(
              "EntityTickManager.checkTicketTick",
              e,
            ),
          );
        }
      }
    }
  }

  static async checkContactTick(payload: any) {
    const _tick = filter(payload, ["entityName", "Contact"]);

    if (_tick.length > 0) {
      const latestTick = contactEntity.query().max("tick");
      const serverLatestTick = _tick[0].tick;

      if (serverLatestTick > latestTick) {
        try {
          let [result]: [any, entityProcessConfig | null] =
            await EntityManager.updateEntity(
              { eventId: Enums.StateEntitySync.InsertOrUpdate },
              Enums.EntitySyncManager[Enums.EntitySyncManager.CheckContactTick],
              "",
              latestTick,
            );

          if (result && result[0] && result[0].length) {
            await contactEntity.insertOrUpdate({ data: result[0] });
          }
        } catch (e) {
          //EntitySyncManager.deleteContactsOfClientsNotInSystem();
          console.error(
            ErrorTemplateBuilder.getCatchText(
              "EntityTickManager.checkContactTick",
              e,
            ),
          );
        }
      }
    }
  }

  static async refillCustomStatus() {
    customStatusEntity.deleteAll();
    try {
      const []: [any, entityProcessConfig | null] =
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.FillCustomStatus],
          "",
          null,
        );
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText("EntitySyncManager.fillClients", e),
      );
    }
  }

  static async updateStoreDataByTick() {
    try {
      let allLatestTick = await ticketService.getServiceProviderAllTick();
      if (allLatestTick && allLatestTick != "")
        allLatestTick.push({
          entityName: "Alert",
          tick: new Date().getTime() * this.seconds + this.timeTick,
        });
      else
        allLatestTick = [
          {
            entityName: "Alert",
            tick: new Date().getTime() * this.seconds + this.timeTick,
          },
        ];
      await this.checkTicketTick(allLatestTick);
      await this.checkTechnicianTick();
      await this.checkContactTick(allLatestTick);
      await this.checkInvoiceTick();
      await this.refillCustomStatus();
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "EntityTickManager.updateStoreDataByTick",
          e,
        ),
      );
    }
  }

  static async checkInvoiceTick() {
    let hasAllInvoicesTag: boolean =
      store.getters["ContactModule/getHasAllInvoicesTag"];
    if (hasAllInvoicesTag) {
      try {
        const latestTick = invoiceEntity.query<any>().max("tick");
        let clientId: number | null =
          store.getters["ContactModule/getClientId"];
        const obj = { tick: latestTick, clientId: clientId };
        await EntityManager.updateEntity(
          { eventId: Enums.StateEntitySync.InsertOrUpdate },
          Enums.EntitySyncManager[Enums.EntitySyncManager.CheckInvoiceTick],
          "",
          obj,
        );
        invoiceEntity.delete((invoice: any) => {
          return invoice.statusId == Enums.InvoiceStatus.Deleted;
        });
      } catch (e) {
        invoiceEntity.delete((invoice: any) => {
          return invoice.statusId == Enums.InvoiceStatus.Deleted;
        });
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "EntityTickManager.checkInvoiceTick",
            e,
          ),
        );
      }
    }
  }
}
